/**
 * The top of the HUD, by layout (hud/hudMode.ts). Edges (mockup 06 A): one
 * full-width pill with the match clock, resources (ResourcesPill), the
 * Cities chips (CityChips), the Upgrades and Upgrading chips, then the
 * viewer's score, the players and the ⚙ settings (SettingsPanel).
 * Contextual (B): the clock, score, resources, cities and upgrades in a pill
 * at the top centre, and the players as dots plus ⚙ at the top right.
 */
import { type ReactNode, useRef } from 'react';
import { type BuildingType, type Command, matchDuration } from '@/game/sim';
import { createIncomeTracker } from '@/game/view/income';
import { clockLabel, clockSeconds } from '@/game/view/matchClock';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { liveScore, liveScoreTitle } from '@/game/view/score';
import { useSimFrame } from '../app/simFrame';
import type { SpendFlash } from '../app/useCommandFeedback';
import type { useQuality } from '../QualitySettings';
import type { UiStore } from '../ui/store';
import { CityChips } from './CityChips';
import type { HudMode } from './hudMode';
import { ResourcesPill } from './ResourcesPill';
import { SettingsPanel } from './SettingsPanel';

export function TopBar({
  ui,
  dispatch,
  quality,
  spends,
  placementMode,
  placementCityId,
  selectedBuildingId,
  onSelectCity,
  onPanToCity,
  upgrades,
  mode,
  onMode,
}: {
  ui: UiStore;
  dispatch: (c: Command) => void;
  quality: ReturnType<typeof useQuality>;
  /** What landed clicks just spent, flashed under the resources. */
  spends: readonly SpendFlash[];
  placementMode: BuildingType | null;
  placementCityId: string | null;
  selectedBuildingId: string | null;
  /** A Cities chip: select the city (while placing, place from it). */
  onSelectCity: (id: string) => void;
  /** A Cities chip's ⌖. */
  onPanToCity: (id: string) => void;
  /** The Upgrades and Upgrading chips (omitted once out). */
  upgrades?: ReactNode;
  mode: HudMode;
  onMode: (mode: HudMode) => void;
}) {
  // Here, not in the pill: switching layout remounts the pill, and the rates would start over.
  const income = useRef(createIncomeTracker()).current;
  const cities = (
    <div className="flex min-w-0 shrink items-center gap-2">
      <CityChips
        activeCityId={placementMode !== null ? placementCityId : selectedBuildingId}
        placementMode={placementMode}
        onSelectCity={onSelectCity}
        onPanToCity={onPanToCity}
        ui={ui}
        dispatch={dispatch}
      />
      {upgrades}
    </div>
  );
  const settings = (
    <SettingsPanel
      ui={ui}
      quality={quality}
      mode={mode}
      onMode={onMode}
      onEndMatch={() => dispatch({ type: 'endMatch' })}
    />
  );
  if (mode === 'contextual') {
    return (
      <>
        <div className="pill absolute left-1/2 top-3 z-20 flex h-10 max-w-[calc(100vw-20rem)] -translate-x-1/2 items-center gap-3 px-4 text-xs">
          <MatchClock />
          <ScoreChip />
          <ResourcesPill spends={spends} tracker={income} />
          {cities}
        </div>
        <div className="absolute right-3 top-3 z-20 flex items-center gap-2">
          <span className="pill flex h-8 items-center px-3 text-xs">
            <Players names={false} />
          </span>
          <span className="pill flex h-8 w-8 items-center justify-center">{settings}</span>
        </div>
      </>
    );
  }
  return (
    <div className="pill absolute inset-x-3 top-3 z-20 flex h-10 min-w-0 items-center gap-3 pl-4 pr-1 text-xs">
      <MatchClock />
      <Separator />
      <ResourcesPill spends={spends} tracker={income} />
      <Separator />
      {cities}
      <span className="flex-1" />
      <ScoreChip />
      <Players />
      <Separator />
      {settings}
    </div>
  );
}

function Separator() {
  return <span aria-hidden="true" className="h-5 w-px shrink-0 bg-line" />;
}

/** Time left, or time played with no limit (`clockSeconds`). */
export function MatchClock() {
  const { view: state, rules } = useSimFrame();
  const duration = matchDuration(state, rules);
  return (
    <span
      title={
        duration === null
          ? 'No time limit: the match ends with the last player standing, or when the host ends it'
          : 'Time left'
      }
      className="shrink-0 text-sm font-medium tabular-nums"
    >
      {clockLabel(clockSeconds(state, rules, state.elapsed))}
      {duration === null && (
        <span className="ml-1.5 text-xs font-normal text-fg-subtle">no limit</span>
      )}
    </span>
  );
}

/** The viewer's live score (own data only; the real state, so a disguised rival spy doesn't count). */
export function ScoreChip() {
  const { view: state, real, playerId, rules } = useSimFrame();
  if (state.players[playerId]?.eliminated) return null;
  const score = liveScore(real, rules, playerId);
  return (
    <span
      title={`${liveScoreTitle(score)}. The highest score wins at the time limit, or when the host ends the match`}
      className="chip shrink-0 tabular-nums"
    >
      ★ {Math.round(score.total).toLocaleString('en-GB')}
    </span>
  );
}

/** Each player as a colour dot and name (`names: false`: dots only, the name on hover). */
export function Players({ names = true }: { names?: boolean }) {
  const { view: state, playerId, playerOrder, playerName } = useSimFrame();
  return (
    <span className="flex min-w-0 shrink items-center gap-2.5">
      {playerOrder.map((pid) => {
        const out = state.players[pid]?.eliminated ?? false;
        const label = `${playerName(pid)}${pid === playerId ? ' (you)' : ''}${out ? ' (eliminated)' : ''}`;
        return (
          <span
            key={pid}
            title={label}
            className={`flex min-w-0 items-center gap-1 ${pid === playerId ? 'font-medium text-fg' : 'text-fg-muted'} ${out ? 'line-through opacity-60' : ''}`}
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{ background: PLAYER_COLORS[colorIndexFor(pid, playerOrder)] }}
            />
            {names && <span className="max-w-24 truncate">{playerName(pid)}</span>}
          </span>
        );
      })}
    </span>
  );
}
